import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GlobeIcon, SmartphoneIcon, ZapIcon, TargetIcon, SearchIcon, ArrowRightIcon, CheckCircleIcon, CodeIcon, ChevronDownIcon, BuildingIcon, StarIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { CodeTypingEffect } from '../components/CodeTypingEffect';
import { SEOHead } from '../components/SEOHead';
export function WebDesignSeoPage() {
  const faqs = [{
    question: 'Do I need a new website to work with you?',
    answer: 'Not necessarily. If your current website is fast, mobile-friendly, and converts well, we can often just implement our AI lead conversion tools and ad management. However, if your site is slow or outdated, a new conversion-focused site will significantly improve your ad ROI.'
  }, {
    question: 'What makes your websites different from a standard template?',
    answer: 'We build websites specifically as conversion assets for home service businesses. They are designed to load instantly, look perfect on mobile, and guide visitors directly toward booking an estimate or making a call.'
  }, {
    question: 'Do you provide ongoing SEO services?',
    answer: 'Yes, but we focus on local SEO and GEO (Generative Engine Optimization) as a support layer for discoverability. We ensure your site is structured correctly, loads fast, and has crawlable service content so local customers and AI search engines can find you.'
  }, {
    question: 'How long does it take to build a new website?',
    answer: 'A standard conversion website typically takes 2-4 weeks from kickoff to launch, depending on how quickly we receive your branding assets and approvals.'
  }, {
    question: 'Are your websites mobile-friendly?',
    answer: 'Yes. Over 70% of local service searches happen on mobile devices. We design mobile-first, ensuring that click-to-call buttons and lead forms are immediately accessible on small screens.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-2937">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-2938">
          <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-2939">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-2940" />
        </button>
        <AnimatePresence data-id="element-2941">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-2942">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2943">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-2944">
      <SEOHead title="Web Design & Local SEO | Conversion Websites | Viridia Analytics" description="We build fast, mobile-first websites designed to convert local traffic into booked jobs. Supported by local SEO and GEO for discoverability." canonical="/web-design-seo" keywords="web design Phoenix, local SEO Phoenix, marketing agency phoenix, contractor websites, HVAC websites, painter websites, landscaper websites, window installer websites, mobile-first website, conversion-focused design, local search optimization, website development Phoenix" faq={faqs} data-id="element-2945" />
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-2946">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-2947">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-2948"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-2949">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2950">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-2951">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-2952">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-2953">
                  WEB DESIGN & SEO
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-2954">
                  Turn Searches Into{' '}
                  <span className="text-viridia" data-id="element-2955">Customers</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2956">
                  Your website should not just be a digital brochure. We build
                  fast, mobile-first websites that act as conversion
                  infrastructure, supported by local SEO to ensure you are found
                  when customers are searching.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-2957">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-2958">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-2959">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration - Browser Mockup */}
            <div className="relative hidden lg:block" data-id="element-2960">
              <motion.div initial={{
              opacity: 0,
              y: 40,
              rotateX: 10
            }} animate={{
              opacity: 1,
              y: 0,
              rotateX: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} style={{
              perspective: '1000px'
            }} className="w-full" data-id="element-2961">
                <div className="bg-viridia-surface border border-gray-700 rounded-xl overflow-hidden shadow-2xl transform rotate-y-[-5deg] rotate-z-[2deg]" data-id="element-2962">
                  {/* Browser Chrome */}
                  <div className="bg-gray-900 px-4 py-3 flex items-center border-b border-gray-800" data-id="element-2963">
                    <div className="flex space-x-2" data-id="element-2964">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-2965"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-2966"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-2967"></div>
                    </div>
                    <div className="mx-auto bg-gray-800 text-gray-400 text-xs px-4 py-1 rounded-md flex items-center" data-id="element-2968">
                      <SearchIcon className="w-3 h-3 mr-2" data-id="element-2969" />
                      phoenix-local-services.com
                    </div>
                  </div>
                  {/* Real-time Coding Effect */}
                  <CodeTypingEffect data-id="element-2970" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD SECTION */}
      <section className="py-24 bg-viridia-light" data-id="element-2971">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2972">
          <ScrollReveal data-id="element-2973">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2974">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2975">
                Websites Built to Convert
              </h2>
              <p className="text-lg text-gray-600" data-id="element-2976">
                Not just pretty - built for speed, mobile-first, and optimized
                to turn visitors into paying customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2977">
            <ScrollReveal delay={0.1} data-id="element-2978">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2979">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2980">
                  <ZapIcon className="w-7 h-7" data-id="element-2981" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2982">
                  Speed-Optimized
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2983">
                  Lightning-fast load times that keep visitors engaged and
                  improve your Google ranking. No bloated templates.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2984">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2985">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2986">
                  <SmartphoneIcon className="w-7 h-7" data-id="element-2987" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2988">
                  Mobile-First Design
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2989">
                  Over 70% of your customers browse on their phone. Every site
                  we build looks and works perfectly on mobile.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-2990">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2991">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2992">
                  <TargetIcon className="w-7 h-7" data-id="element-2993" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2994">
                  Conversion-Focused
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2995">
                  Strategic layouts, clear CTAs, and trust signals designed to
                  turn visitors into leads and booked jobs.
                </p>
              </div>
            </ScrollReveal>
          </div>

          {/* MOBILE-FIRST SHOWCASE */}
          <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-2996">
            <ScrollReveal data-id="element-2997">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-2998">
                Mobile-First
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2999">
                Designed for the screen in their hand
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-3000">
                Over 70% of local service searches happen on a phone. We build
                every site mobile-first, so the moment a homeowner lands on your
                page they can tap to call, request a quote, or book - no
                pinching, zooming, or hunting.
              </p>
              <ul className="space-y-3" data-id="element-3001">
                {['Tap-to-call and click-to-text buttons within thumb reach', 'Fast-loading pages even on cellular data', 'Lead forms that are effortless to fill out on mobile'].map((item, i) => <li key={i} className="flex items-start" data-id="element-3002">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-3003" />
                    <span className="text-gray-700" data-id="element-3004">{item}</span>
                  </li>)}
              </ul>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-3005">
              <div className="flex justify-center" data-id="element-3006">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.7
              }} className="relative" data-id="element-3007">
                  <motion.div animate={{
                  y: [0, -12, 0]
                }} transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} aria-label="Mobile-first website preview on a smartphone" role="img" className="relative w-[280px] h-[580px] rounded-[3rem] bg-gray-900 p-3 shadow-2xl border border-gray-800" data-id="element-3008">
                    {/* Notch */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-20" data-id="element-3009"></div>
                    {/* Screen */}
                    <div className="relative w-full h-full rounded-[2.3rem] overflow-hidden bg-white" data-id="element-3010">
                      {/* Site nav */}
                      <div className="flex items-center justify-between px-4 pt-6 pb-3 bg-viridia-black" data-id="element-3011">
                        <span className="text-white font-bold text-sm" data-id="element-3012">
                          Summit HVAC
                        </span>
                        <div className="flex flex-col gap-1" data-id="element-3013">
                          <span className="block w-5 h-0.5 bg-white" data-id="element-3014"></span>
                          <span className="block w-5 h-0.5 bg-white" data-id="element-3015"></span>
                          <span className="block w-5 h-0.5 bg-white" data-id="element-3016"></span>
                        </div>
                      </div>
                      {/* Hero */}
                      <div className="bg-viridia-black px-4 pb-6 pt-2" data-id="element-3017">
                        <h4 className="text-white font-extrabold text-lg leading-tight mb-2" data-id="element-3018">
                          Fast, Reliable AC Repair in Phoenix
                        </h4>
                        <p className="text-gray-400 text-[11px] mb-4 leading-relaxed" data-id="element-3019">
                          Same-day service. Licensed & insured. Upfront pricing
                          with no surprises.
                        </p>
                        <div className="flex items-center gap-2" data-id="element-3020">
                          <div className="flex-1 bg-viridia text-white text-center text-[11px] font-bold py-2 rounded-lg" data-id="element-3021">
                            Call Now
                          </div>
                          <div className="flex-1 bg-white/10 text-white text-center text-[11px] font-bold py-2 rounded-lg border border-white/20" data-id="element-3022">
                            Get a Quote
                          </div>
                        </div>
                      </div>
                      {/* Trust strip */}
                      <div className="flex items-center justify-center gap-1 py-2 bg-gray-50 border-y border-gray-100" data-id="element-3023">
                        {[0, 1, 2, 3, 4].map(s => <StarIcon key={s} className="w-3 h-3 text-yellow-400 fill-current" data-id="element-3024" />)}
                        <span className="text-[10px] text-gray-500 ml-1" data-id="element-3025">
                          4.9 · 200+ reviews
                        </span>
                      </div>
                      {/* Services */}
                      <div className="px-4 py-4" data-id="element-3026">
                        <div className="text-gray-900 font-bold text-xs mb-3" data-id="element-3027">
                          Our Services
                        </div>
                        <div className="grid grid-cols-2 gap-2" data-id="element-3028">
                          {['AC Repair', 'Installation', 'Maintenance', 'Tune-Ups'].map(svc => <div key={svc} className="bg-gray-50 border border-gray-100 rounded-lg p-2" data-id="element-3029">
                              <div className="w-5 h-5 rounded bg-viridia/10 mb-1.5" data-id="element-3030"></div>
                              <div className="text-[10px] font-semibold text-gray-700" data-id="element-3031">
                                {svc}
                              </div>
                            </div>)}
                        </div>
                      </div>
                      {/* Sticky CTA bar */}
                      <div className="absolute bottom-0 left-0 right-0 bg-viridia px-4 py-3 flex items-center justify-between" data-id="element-3032">
                        <span className="text-white text-[11px] font-bold" data-id="element-3033">
                          (623) 300-0708
                        </span>
                        <span className="bg-white text-viridia-dark text-[10px] font-bold px-3 py-1 rounded-full" data-id="element-3034">
                          Tap to Call
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* SEO & GEO SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-3035">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3036">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-3037">
            <ScrollReveal data-id="element-3038">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-3039">
                Discoverability Support
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-3040">
                A great website is useless if no one can find it. We provide
                local SEO and GEO (Generative Engine Optimization) support to
                ensure your business is visible when it matters.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-3041">
                <li className="flex items-start" data-id="element-3042">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-3043" />
                  <span className="text-gray-300" data-id="element-3044">
                    Crawlable service content for search engines
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3045">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-3046" />
                  <span className="text-gray-300" data-id="element-3047">
                    Local relevance optimization (Google Business Profile
                    integration)
                  </span>
                </li>
                <li className="flex items-start" data-id="element-3048">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-3049" />
                  <span className="text-gray-300" data-id="element-3050">
                    Structured data (Schema markup) for AI discoverability
                  </span>
                </li>
              </ul>
            </ScrollReveal>

            <ScrollReveal direction="left" data-id="element-3051">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 shadow-2xl" data-id="element-3052">
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-800" data-id="element-3053">
                  <div className="w-12 h-12 rounded-xl bg-viridia/10 flex items-center justify-center" data-id="element-3054">
                    <CodeIcon className="w-6 h-6 text-viridia" data-id="element-3055" />
                  </div>
                  <div data-id="element-3056">
                    <h3 className="text-xl font-bold text-white" data-id="element-3057">
                      Technical Foundation
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-3058">
                      Built for modern search
                    </p>
                  </div>
                </div>
                <div className="space-y-4" data-id="element-3059">
                  <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-3060">
                    <div className="text-sm font-medium text-white mb-1" data-id="element-3061">
                      Semantic HTML
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-3062">
                      Clear heading structures and accessible markup.
                    </div>
                  </div>
                  <div className="bg-viridia-black p-4 rounded-lg border border-gray-800" data-id="element-3063">
                    <div className="text-sm font-medium text-white mb-1" data-id="element-3064">
                      Fast Hosting
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-3065">
                      Deployed on global edge networks for instant load times.
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-3066">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3067">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3068">
            <ScrollReveal data-id="element-3069">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-3070">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-3071">
                Our web design services are for local service businesses that
                need a strong digital foundation to support their ad campaigns
                and lead conversion efforts.
              </p>
              <ul className="space-y-4" data-id="element-3072">
                <li className="flex items-start gap-3" data-id="element-3073">
                  <BuildingIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-3074" />
                  <div data-id="element-3075">
                    <h4 className="font-bold text-white" data-id="element-3076">Local Contractors</h4>
                    <p className="text-sm text-gray-400" data-id="element-3077">
                      HVAC, painters, landscapers, window installers, and other
                      businesses that rely on local search to find customers.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-3078">
                  <ZapIcon className="w-6 h-6 text-viridia flex-shrink-0 mt-0.5" data-id="element-3079" />
                  <div data-id="element-3080">
                    <h4 className="font-bold text-white" data-id="element-3081">
                      Businesses Running Ads
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-3082">
                      If you are paying for traffic, you need a website that
                      actually converts that traffic into leads.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-3083">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-3084">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-3085">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-3086">
                  <Link to="/ad-campaigns" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-3087">
                    <div className="font-bold text-white mb-1" data-id="element-3088">
                      Ad Campaigns
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-3089">
                      Drive high-intent traffic to your new conversion website.
                    </div>
                  </Link>
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-3090">
                    <div className="font-bold text-white mb-1" data-id="element-3091">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-3092">
                      Ensure every lead generated by your site gets an instant
                      response.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-3093">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-3094">
          <ScrollReveal data-id="element-3095">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-3096">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3097">
          <ScrollReveal delay={0.2} data-id="element-3098">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-3099">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-3100" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-3101">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3102">
          <ScrollReveal data-id="element-3103">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-3104">
              Ready for a site that converts?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-3105">
              Book a strategy call to see how we can build a conversion-focused
              website for your business.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300" data-id="element-3106">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-3107" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}